import React from "react";
import styled from "@emotion/styled";
import {
  backgroundDark,
  lightBorderColor,
  tooltipHeaderBackgroundColor,
} from "../styles";
import {
  text,
  surface,
  interactive,
  shadow,
  zIndex,
  borderRadius,
  fontSize,
  fontWeight,
  spacing,
  lineHeight,
  transition,
  dataViz,
  dataVizLayout,
} from "@atlas-int/tokens";

export enum TooltipTheme {
  Light = "light",
  Dark = "dark",
}

export enum TooltipPosition {
  Automatic = "automatic",
  Bottom = "bottom",
  Right = "right",
}

// Container for tooltip contents
export const TooltipContentsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

// Header section with optional background color
export const TooltipHeader = styled.div<{ $backgroundColor?: string }>`
  width: 100%;
  padding: ${spacing[4]};
  background-color: ${({ $backgroundColor }) =>
    $backgroundColor ? `${$backgroundColor}4D` : tooltipHeaderBackgroundColor};
  box-sizing: border-box;
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
`;

// Product code label styling
export const ProductCodeLabel = styled.span`
  font-size: ${fontSize.sm};
  color: ${dataViz.textSecondary};
`;

// Body section
export const TooltipBody = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${spacing[4]};
  box-sizing: border-box;
  font-size: ${fontSize.sm};
`;

// Row in tooltip body
export const TooltipBodyRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: ${spacing[1]};
`;

// Label in tooltip row
export const TooltipBodyRowLabel = styled.div`
  width: 75%;
`;

// Value in tooltip row
export const TooltipBodyRowValue = styled.div`
  font-weight: ${fontWeight.bold};
  margin-left: auto;
`;

// Explore more section container
export const TooltipExploreMoreContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-top: ${spacing[6]};
  & h2 {
    font-size: inherit;
    font-style: italic;
    display: block;
    flex: 1 0 100%;
    font-weight: unset;
    margin-bottom: ${spacing[2]};
    margin-top: ${spacing[0]};
  }
`;

// Icon styling
const TooltipButtonIcon = styled.img`
  flex-shrink: 0;
  height: ${dataVizLayout.tooltipIconSize}px;
  width: ${dataVizLayout.tooltipIconSize}px;
  object-fit: contain;
`;

// Base button styling for exploring more
const TooltipExploreMoreButtonBase = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing[4]};
  padding: ${spacing[4]} ${spacing[6]};
  margin-right: ${spacing[4]};
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
  color: inherit;
  border-radius: ${borderRadius.base};

  &:hover:not(:disabled) {
    background-color: ${surface.panel};
  }
`;

// Button component with icon for exploring more
export const TooltipExploreMoreButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & { icon: string }
> = ({ children, icon, ...props }) => (
  <TooltipExploreMoreButtonBase {...props}>
    <TooltipButtonIcon src={icon} alt="" aria-hidden="true" />
    {children}
  </TooltipExploreMoreButtonBase>
);

// Sector label with color indicator
export const SectorLabelWithColorIcon = styled.div<{
  $fillColor: string | undefined;
}>`
  font-weight: inherit;
  display: flex;
  align-items: center;
  width: max-content;

  &::before {
    display: ${({ $fillColor }) => ($fillColor ? `inline-block` : `none`)};
    width: 12px;
    height: 12px;
    content: "";
    background-color: ${({ $fillColor }) =>
      $fillColor ? $fillColor : interactive.muted};
    margin-right: ${spacing[1]};
  }
`;

// Show more/less button
export const ShowMoreLessButton = styled.div`
  cursor: pointer;
  margin: ${spacing[2]} ${spacing[0]} ${spacing[0]} ${spacing[0]};
`;

// Base tooltip container
export const farEndOfScreenToggleClass = "tooltip-at-right-end-of-screen";
export const arrowContainerClassName = "tooltip-arrow-container-class";
export const flipArrowClassName = "tooltip-arrow-flip-side-class";
export const leftArrowClassName = "tooltip-arrow-point-left-class";
export const rightArrowClassName = "tooltip-arrow-point-right-class";

export const TooltipBase = styled.div<{
  $theme: TooltipTheme | undefined;
  $overrideStyles: boolean | undefined;
  $isFixed: boolean;
  $flexWidth?: boolean;
  $zIndex?: number;
}>`
  position: fixed;
  z-index: ${({ $zIndex }) => $zIndex ?? zIndex.overlay};
  width: max-content;
  max-width: ${dataVizLayout.tooltipWidth}px;
  ${({ $flexWidth }) =>
    !$flexWidth && `min-width: ${dataVizLayout.tooltipWidth}px;`}
  font-size: ${fontSize.xs};
  line-height: ${lineHeight.normal};
  text-transform: none;
  ${({ $overrideStyles }) =>
    $overrideStyles
      ? `padding-bottom: ${spacing[4]};`
      : `padding: ${spacing[4]};`}
  opacity: 0;
  transition: opacity ${transition.base};
  color: ${text.primary};
  background-color: ${({ $theme }) =>
    $theme === TooltipTheme.Dark ? backgroundDark : surface.white};
  color: ${({ $theme }) =>
    $theme === TooltipTheme.Dark ? surface.white : text.primary};
  border: 1px solid
    ${({ $theme }) =>
      $theme === TooltipTheme.Dark ? backgroundDark : lightBorderColor};
  border-radius: ${borderRadius.base};
  box-shadow: ${shadow.sm};
  pointer-events: ${({ $isFixed }) => ($isFixed ? "all" : "none")};
  box-sizing: border-box;

  &.${farEndOfScreenToggleClass} .${arrowContainerClassName} {
    justify-content: flex-end;
    padding-right: ${spacing[6]};
    box-sizing: border-box;
  }

  &.${flipArrowClassName} .${arrowContainerClassName} {
    transform: translate(0, -100%);
    top: 0;

    div {
      &:before {
        top: -1px;
        transform: rotate(180deg);
      }

      &:after {
        transform: rotate(180deg);
      }
    }
  }

  &.${leftArrowClassName} .${arrowContainerClassName} {
    transform: translate(-50%, 0);
    top: 50%;
    left: -6px;

    div {
      &:before {
        top: -1px;
        transform: rotate(90deg);
      }

      &:after {
        transform: rotate(90deg);
      }
    }
  }

  &.${rightArrowClassName} .${arrowContainerClassName} {
    transform: translate(50%, 0);
    top: 50%;
    right: -4px;

    div {
      &:before {
        top: -1px;
        transform: rotate(-90deg);
      }

      &:after {
        transform: rotate(-90deg);
      }
    }
  }
`;

export const ArrowContainer = styled.div<{
  $position: TooltipPosition | undefined;
}>`
  width: 100%;
  height: ${dataVizLayout.arrowHeight}px;
  display: flex;
  justify-content: center;
  position: absolute;
  pointer-events: none;
  ${({ $position }) => {
    if ($position === TooltipPosition.Bottom) {
      return `
        top: 0;
        transform: translateY(-100%);
      `;
    } else if ($position === TooltipPosition.Right) {
      return `
        top: 50%;
        transform: translate(-100%, -50%);
      `;
    } else {
      return `
        bottom: 0;
        transform: translateY(100%);
      `;
    }
  }}

  .${flipArrowClassName} & {
    top: auto;
    bottom: 100%;
    transform: none;
  }
`;

export const Arrow = styled.div<{
  $theme: TooltipTheme | undefined;
  $position: TooltipPosition | undefined;
}>`
  position: relative;
  width: ${dataVizLayout.arrowWidth}px;
  height: ${dataVizLayout.arrowHeight}px;

  &:before {
    content: "";
    position: absolute;
    top: -1px;
    left: 0;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid
      ${({ $theme }) =>
        $theme === TooltipTheme.Dark ? backgroundDark : surface.white};
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.15));

    .${flipArrowClassName} & {
      transform: rotate(180deg);
      top: 1px;
    }
  }
`;

// Marker lines for charts
export const MarkerLine = styled.span`
  position: absolute;
  margin-left: -1.5px;
  width: 3px;
  height: auto;
  background-color: ${text.primary};
  pointer-events: none;
  border-radius: 0;
  content: "";
  z-index: ${zIndex.tooltip};
`;

export const HorizontalMarkerLine = styled.span`
  position: absolute;
  height: 1px;
  width: auto;
  background-color: ${text.primary};
  pointer-events: none;
  border-radius: 0;
  content: "";
  z-index: ${zIndex.tooltip};
  opacity: 0.75;
`;

export const AxisIndicatorLine = styled.span`
  position: absolute;
  margin-left: -0.5px;
  width: 1px;
  height: auto;
  background-color: ${text.primary};
  pointer-events: none;
  content: "";
  z-index: ${zIndex.tooltip};
  opacity: 0.75;
`;

export const AxisAnnotation = styled.div`
  position: absolute;
  background-color: ${dataViz.primaryAction};
  color: ${text.onDark};
  border-radius: ${borderRadius.base};
  padding: ${spacing[1]} ${spacing[1]};
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.medium};
  pointer-events: none;
  z-index: ${zIndex.navigation};
  white-space: nowrap;
  box-shadow: ${shadow.sm};
`;
